import React, { useState, useEffect } from 'react';
import { ChevronLeft, Package, MapPin, Plus, Truck, X, ShoppingBag, Boxes } from 'lucide-react';
import axios from 'axios';
import InventoryOverview from './inventory/InventoryOverview';
import DeliveryInventoryDialog from './inventory/DeliveryInventoryDialog';
import ComboModal from './inventory/ComboModal';
import { PackagesInFastmax } from './inventory/PackagesInFastmax';

function Inventory({ userId, setActiveComponent }) {
  const [inventoryData, setInventoryData] = useState({
    items: [],
    deliveries: [],
    counts: { deliveries: 0, packages: 0, units: 0 }
  });
  const [categories, setCategories] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comboModalOpen, setComboModalOpen] = useState(false);
  const [fastmaxModalOpen, setFastmaxModalOpen] = useState(false);
  
  // Active form state
  const [activeForm, setActiveForm] = useState(null); // 'product', 'location', 'stock'
  
  // Product form
  const [sku, setSku] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);

  // Location form
  const [locName, setLocName] = useState('');
  const [locAddress, setLocAddress] = useState('');
  const [locLoading, setLocLoading] = useState(false);

  // Stock form
  const [stockSku, setStockSku] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [stockLoading, setStockLoading] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (!userId) return;
    fetchTodayPackages();
    fetchCategories();
    fetchLocations();
  }, [userId]);

  function handleComboCreated(combo) {
    console.log('Combo created:', combo);
    // Optionally refresh data or show success message
  }

  async function fetchTodayPackages() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/today_packages`, {
        params: { user_id: userId }
      });
      setInventoryData(data); // <— keep full shape
    } catch (error) {
      console.error("Error fetching today's packages:", error);
    }
  }

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function fetchLocations() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/locations`, {
        params: { user_id: userId }
      });
      setLocationOptions(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }

  async function handleCreateProduct() {
    if (!sku.trim() || !title.trim() || price === '') {
      alert('SKU, título y precio son obligatorios');
      return;
    }

    setProdLoading(true);
    try {
      const payload = {
        sku: sku.trim(),
        title: title.trim(),
        description: description.trim(),
        price: Number(price),
        user_id: userId,
        category_ids: selectedCats.map(cat => cat.id)
      };

      const { data } = await axios.post(`${baseUrl}/api/create_product`, payload);
      alert(`Producto creado con ID ${data.id}`);
      
      // Clear form
      setSku('');
      setTitle('');
      setDescription('');
      setPrice('');
      setSelectedCats([]);
      setActiveForm(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Error al crear producto');
    } finally {
      setProdLoading(false);
    }
  }

  async function handleCreateLocation() {
    if (!locName.trim()) {
      alert('El nombre de la ubicación es obligatorio');
      return;
    }

    setLocLoading(true);
    try {
      const payload = {
        name: locName.trim(),
        address: locAddress.trim(),
        user_id: userId
      };

      const { data } = await axios.post(`${baseUrl}/api/create_location`, payload);
      alert(`Ubicación creada con ID ${data.id}`);
      
      // Clear form and refresh locations
      setLocName('');
      setLocAddress('');
      setActiveForm(null);
      fetchLocations();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Error al crear ubicación');
    } finally {
      setLocLoading(false);
    }
  }

  async function handleAddStock() {
    if (!stockSku.trim() || !selectedLocation || !quantity) {
      alert('SKU, ubicación y cantidad son obligatorios');
      return;
    }

    setStockLoading(true);
    try {
      const payload = {
        user_id: userId,
        product_sku: stockSku.trim(),
        location_id: selectedLocation.id,
        quantity: Number(quantity),
        transaction_type: 'MANUAL'
      };

      const { data } = await axios.post(`${baseUrl}/api/add_stock`, payload);
      alert(`Stock agregado. Nueva cantidad: ${data.new_quantity}`);
      
      // Clear form
      setStockSku('');
      setSelectedLocation(null);
      setQuantity('');
      setActiveForm(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Error al agregar stock');
    } finally {
      setStockLoading(false);
    }
  }

  function toggleCategorySelection(category) {
    if (selectedCats.some(cat => cat.id === category.id)) {
      setSelectedCats(selectedCats.filter(cat => cat.id !== category.id));
    } else {
      setSelectedCats([...selectedCats, category]);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Options')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
      
            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Inventario
            </h1>
      
            <div className="flex items-center gap-2">
              <button
                onClick={() => setComboModalOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                title="Gestionar Combos"
              >
                <ShoppingBag className="w-5 h-5" />
              </button>
              <button
                onClick={() => setDialogOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                title="Entregas de hoy"
              >
                <Truck className="w-5 h-5" />
              </button>
      
              {/* ✅ NEW: Paquetes en Fast Max button */}
              <button
                onClick={() => setFastmaxModalOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                title="Paquetes en Fast Max"
              >
                <Boxes className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveForm(activeForm === 'product' ? null : 'product')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 font-montserrat text-sm ${
                activeForm === 'product'
                  ? 'bg-[#B1336E] text-white shadow-md'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-[#B1336E] hover:text-[#B1336E]'
              }`}
            >
              <Package className="w-4 h-4" />
              <span>Nuevo Producto</span>
            </button>

            <button
              onClick={() => setActiveForm(activeForm === 'location' ? null : 'location')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 font-montserrat text-sm ${
                activeForm === 'location'
                  ? 'bg-[#E6B43C] text-white shadow-md'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-[#E6B43C] hover:text-[#E6B43C]'
              }`}
            >
              <MapPin className="w-4 h-4" />
              <span>Nueva Ubicación</span>
            </button>

            <button
              onClick={() => setActiveForm(activeForm === 'stock' ? null : 'stock')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 font-montserrat text-sm ${
                activeForm === 'stock'
                  ? 'bg-[#67A43C] text-white shadow-md'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-[#67A43C] hover:text-[#67A43C]'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>Agregar Stock</span>
            </button>
          </div>
        </div>
      </div>

      {/* Forms Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Product Form */}
        {activeForm === 'product' && (
          <div className="my-6 bg-white rounded-2xl shadow-sm border-2 border-[#B1336E] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#B1336E] font-montserrat">Crear Nuevo Producto</h3>
              <button
                onClick={() => setActiveForm(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="SKU *"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              />
              
              <input
                type="text"
                placeholder="Título *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              />
              
              <input
                type="number"
                placeholder="Precio *"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm"
              />
              
              <textarea
                placeholder="Descripción"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="2"
                className="sm:col-span-2 lg:col-span-3 px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat text-sm resize-none"
              />
            </div>

            {/* Categories */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
                Categorías
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategorySelection(cat)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-montserrat transition-colors ${
                      selectedCats.some(c => c.id === cat.id)
                        ? 'bg-[#B1336E] text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleCreateProduct}
              disabled={prodLoading}
              className="mt-4 w-full bg-[#B1336E] hover:bg-[#B1336E]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
            >
              {prodLoading ? 'Guardando...' : 'Guardar Producto'}
            </button>
          </div>
        )}

        {/* Location Form */}
        {activeForm === 'location' && (
          <div className="my-6 bg-white rounded-2xl shadow-sm border-2 border-[#E6B43C] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#E6B43C] font-montserrat">Crear Nueva Ubicación</h3>
              <button
                onClick={() => setActiveForm(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Nombre de Almacén *"
                value={locName}
                onChange={(e) => setLocName(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E6B43C]/20 focus:border-[#E6B43C] font-montserrat text-sm"
              />
              
              <input
                type="text"
                placeholder="Dirección"
                value={locAddress}
                onChange={(e) => setLocAddress(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E6B43C]/20 focus:border-[#E6B43C] font-montserrat text-sm"
              />
            </div>

            <button
              onClick={handleCreateLocation}
              disabled={locLoading}
              className="mt-4 w-full bg-[#E6B43C] hover:bg-[#E6B43C]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
            >
              {locLoading ? 'Guardando...' : 'Guardar Ubicación'}
            </button>
          </div>
        )}

        {/* Stock Form */}
        {activeForm === 'stock' && (
          <div className="my-6 bg-white rounded-2xl shadow-sm border-2 border-[#67A43C] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#67A43C] font-montserrat">Agregar Stock</h3>
              <button
                onClick={() => setActiveForm(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="SKU del Producto *"
                value={stockSku}
                onChange={(e) => setStockSku(e.target.value)}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#67A43C]/20 focus:border-[#67A43C] font-montserrat text-sm"
              />
              
              <select
                value={selectedLocation?.id || ''}
                onChange={(e) => {
                  const loc = locationOptions.find(l => l.id === Number(e.target.value));
                  setSelectedLocation(loc || null);
                }}
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#67A43C]/20 focus:border-[#67A43C] font-montserrat text-sm bg-white"
              >
                <option value="">Selecciona Ubicación *</option>
                {locationOptions.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              
              <input
                type="number"
                placeholder="Cantidad *"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="1"
                className="px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#67A43C]/20 focus:border-[#67A43C] font-montserrat text-sm"
              />
            </div>

            <button
              onClick={handleAddStock}
              disabled={stockLoading}
              className="mt-4 w-full bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
            >
              {stockLoading ? 'Guardando...' : 'Agregar Stock'}
            </button>
          </div>
        )}
      </div>

      {/* Inventory Overview */}
      <InventoryOverview userId={userId} />
      
      <PackagesInFastmax
        isOpen={fastmaxModalOpen}
        onClose={() => setFastmaxModalOpen(false)}
        userId={userId}   // the userId prop passed down to Inventory.jsx
      />

      {/* Delivery Dialog */}
      <DeliveryInventoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        data={inventoryData}
      />
      
      {comboModalOpen && (
        <ComboModal
          userId={userId}
          onClose={() => setComboModalOpen(false)}
          onComboCreated={handleComboCreated}
        />
      )}
    </div>
  );
}

export default Inventory;